import { forwardRef, useState, useEffect } from "react";
import styled from "styled-components";
import { useControllableState } from "../../hooks";
import Flex from "../flex";
import Box from "../box";
import Button from "../button";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DAYS = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday"
};
const HOURS = {
  0: "12 AM",
  1: "1 AM",
  2: "2 AM",
  3: "3 AM",
  4: "4 AM",
  5: "5 AM",
  6: "6 AM",
  7: "7 AM",
  8: "8 AM",
  9: "9 AM",
  10: "10 AM",
  11: "11 AM",
  12: "12 PM",
  13: "1 PM",
  14: "2 PM",
  15: "3 PM",
  16: "4 PM",
  17: "5 PM",
  18: "6 PM",
  19: "7 PM",
  20: "8 PM",
  21: "9 PM",
  22: "10 PM",
  23: "11 PM"
};
function getCellColor(_ref) {
  let {
    theme,
    active,
    draggingActive,
    draggingInactive,
    disabled
  } = _ref;
  if (disabled && active) {
    return theme.colors.primary[2];
  }
  if (draggingInactive && active) {
    return theme.colors.alpha[3];
  }
  if (draggingActive) {
    return theme.colors.primary[2];
  }
  if (active) {
    return theme.colors.primary.base;
  }
  return "transparent";
}
const StyledCell = styled.td.withConfig({
  displayName: "day-parts-picker__StyledCell",
  componentId: "sc-jlgwss-0"
})(["cursor:", ";&::before{background:", ";border-radius:", "px;margin:8px;height:10px;display:block;content:\"\";}"], p => p.disabled ? "not-allowed" : "pointer", getCellColor, p => p.theme.radii.base);
const StyledTable = styled.table.withConfig({
  displayName: "day-parts-picker__StyledTable",
  componentId: "sc-jlgwss-1"
})(["width:100%;border-collapse:separate;border-spacing:0;th,td{font-size:", "px;font-weight:normal;color:", ";padding:4px;-webkit-touch-callout:none;user-select:none;}th:first-child{position:-webkit-sticky;position:sticky;left:0;z-index:1;}thead{border:", ";th{background:", ";text-align:center;vertical-align:middle;}}tbody{tr:nth-child(odd){background:", ";}tr:nth-child(even){background:", ";}th:first-child{background:inherit;}th,td{border:", ";text-align:left;}td{width:100px;margin:4px;padding:0;}}"], p => p.theme.fontSizes.sm, p => p.theme.colors.text, p => p.theme.borders.split, p => p.theme.colors.gray[2], p => p.theme.colors.gray[1], p => p.theme.colors.gray[0], p => p.theme.borders.split);
const ScrollBox = styled(Box).withConfig({
  displayName: "day-parts-picker__ScrollBox",
  componentId: "sc-jlgwss-2"
})(["overflow:auto;&::-webkit-scrollbar{width:6px;height:6px;}&::-webkit-scrollbar-track,&::-webkit-scrollbar-corner{background:transparent;}&::-webkit-scrollbar-thumb{background:", ";border:none;border-radius:6px;}"], p => p.theme.colors.alpha[5]);
function HourLabel(_ref2) {
  let {
    hour,
    value,
    disabled,
    applySelection
  } = _ref2;
  const toggleHour = () => {
    const active = value.filter(el => el.hour === hour).length !== Object.keys(DAYS).length;
    applySelection({
      hour,
      day: "0"
    }, {
      hour,
      day: "6"
    }, active);
  };
  const buttonStyle = {
    fontSize: "12px",
    width: "44px",
    height: "20px",
    padding: "0 4px"
  };
  return /*#__PURE__*/_jsx("th", {
    children: /*#__PURE__*/_jsx(Button, {
      size: "small",
      type: "text",
      disabled: disabled,
      onClick: toggleHour,
      style: buttonStyle,
      children: HOURS[hour]
    })
  });
}
function DayLabel(_ref3) {
  let {
    day,
    value,
    disabled,
    applySelection
  } = _ref3;
  const toggleDay = () => {
    const active = value.filter(el => el.day === day).length !== Object.keys(HOURS).length;
    applySelection({
      day,
      hour: "0"
    }, {
      day,
      hour: "23"
    }, active);
  };
  const buttonStyle = {
    fontSize: "12px",
    width: "100%",
    height: "20px",
    padding: "0 4px"
  };
  return /*#__PURE__*/_jsx("th", {
    children: /*#__PURE__*/_jsx(Button, {
      size: "small",
      type: "text",
      disabled: disabled,
      onClick: toggleDay,
      style: buttonStyle,
      children: DAYS[day]
    })
  });
}
function DataCell(_ref4) {
  let {
    day,
    hour,
    value,
    disabled,
    drag,
    onDragChange
  } = _ref4;
  const active = !!value.find(el => el.day === day && el.hour === hour);
  let draggingActive = false;
  let draggingInactive = false;
  if (drag) {
    const active = !!value.find(el => el.day === drag.start.day && el.hour === drag.start.hour);
    const minDay = Math.min(+drag.start.day, +drag.current.day);
    const maxDay = Math.max(+drag.start.day, +drag.current.day);
    const minHour = Math.min(+drag.start.hour, +drag.current.hour);
    const maxHour = Math.max(+drag.start.hour, +drag.current.hour);
    if (+day >= minDay && +day <= maxDay && +hour >= minHour && +hour <= maxHour) {
      if (active) {
        draggingInactive = true;
      } else {
        draggingActive = true;
      }
    }
  }
  const onMouseDown = event => {
    if (!disabled && event.button === 0) {
      event.preventDefault();
      onDragChange({
        start: {
          day,
          hour
        },
        current: {
          day,
          hour
        }
      });
    }
  };
  const onMouseMove = event => {
    event.preventDefault();
    if (!disabled && drag && (drag.current.day !== day || drag.current.hour !== hour)) {
      onDragChange({
        start: drag.start,
        current: {
          day,
          hour
        }
      });
    }
  };
  return /*#__PURE__*/_jsx(StyledCell, {
    active: active,
    draggingActive: draggingActive,
    draggingInactive: draggingInactive,
    disabled: disabled,
    onMouseDown: onMouseDown,
    onMouseMove: onMouseMove
  });
}
export const DayPartsPicker = /*#__PURE__*/forwardRef((_ref5, ref) => {
  let {
    value,
    onChange,
    defaultValue,
    disabled,
    className
  } = _ref5;
  const [drag, setDrag] = useState(null);
  const [dayParts, setDayParts] = useControllableState(value, onChange, defaultValue ?? []);
  const selectAll = () => {
    const values = [];
    for (let day = 0; day <= 6; day++) {
      for (let hour = 0; hour <= 23; hour++) {
        values.push({
          day: String(day),
          hour: String(hour)
        });
      }
    }
    setDayParts(values);
  };
  const clearAll = () => {
    setDayParts([]);
  };
  const applySelection = (start, end, active) => {
    const minDay = Math.min(+start.day, +end.day);
    const maxDay = Math.max(+start.day, +end.day);
    const minHour = Math.min(+start.hour, +end.hour);
    const maxHour = Math.max(+start.hour, +end.hour);
    const nextValue = dayParts.slice();
    for (let day = minDay; day <= maxDay; day++) {
      for (let hour = minHour; hour <= maxHour; hour++) {
        let cellIndex = nextValue.findIndex(el => el.day === String(day) && el.hour === String(hour));
        if (active && cellIndex === -1) {
          nextValue.push({
            day: String(day),
            hour: String(hour)
          });
        } else if (!active && cellIndex !== -1) {
          nextValue.splice(cellIndex, 1);
        }
      }
    }
    setDayParts(nextValue);
  };
  const releaseDrag = event => {
    event.preventDefault();
    if (drag) {
      const active = !dayParts.find(el => el.day === drag.start.day && el.hour === drag.start.hour);
      applySelection(drag.start, drag.current, active);
      setDrag(null);
    }
  };
  useEffect(() => {
    if (drag) {
      document.addEventListener("mouseup", releaseDrag);
      return () => document.removeEventListener("mouseup", releaseDrag);
    }
    return;
  }, [drag, releaseDrag]);
  return /*#__PURE__*/_jsxs(Flex, {
    ref: ref,
    className: className,
    flexDirection: "column",
    border: "split",
    borderRadius: "base",
    overflow: "hidden",
    children: [/*#__PURE__*/_jsx(Box, {
      position: "relative",
      zIndex: "0",
      children: /*#__PURE__*/_jsx(ScrollBox, {
        children: /*#__PURE__*/_jsxs(StyledTable, {
          children: [/*#__PURE__*/_jsx("thead", {
            children: /*#__PURE__*/_jsxs("tr", {
              children: [/*#__PURE__*/_jsx("th", {
                children: "DAY"
              }), Object.keys(HOURS).map(hour => /*#__PURE__*/_jsx(HourLabel, {
                hour: hour,
                value: dayParts,
                disabled: disabled,
                applySelection: applySelection
              }, hour))]
            })
          }), /*#__PURE__*/_jsx("tbody", {
            children: Object.keys(DAYS).map(day => /*#__PURE__*/_jsxs("tr", {
              children: [/*#__PURE__*/_jsx(DayLabel, {
                day: day,
                value: dayParts,
                disabled: disabled,
                applySelection: applySelection
              }), Object.keys(HOURS).map(hour => /*#__PURE__*/_jsx(DataCell, {
                day: day,
                hour: hour,
                value: dayParts,
                disabled: disabled,
                drag: drag,
                onDragChange: setDrag
              }, hour))]
            }, day))
          })]
        })
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      gap: 2,
      p: 2,
      bg: "gray.0",
      children: [/*#__PURE__*/_jsx(Button, {
        size: "small",
        onClick: selectAll,
        disabled: disabled,
        children: "Select All"
      }), /*#__PURE__*/_jsx(Button, {
        size: "small",
        onClick: clearAll,
        disabled: disabled,
        children: "Clear All"
      })]
    })]
  });
});

/** @deprecated */
function selectAll() {
  const values = [];
  for (let day = 0; day <= 6; day++) {
    for (let hour = 0; hour <= 23; hour++) {
      values.push({
        day: String(day),
        hour: String(hour)
      });
    }
  }
  return values;
}
export default Object.assign(DayPartsPicker, {
  selectAll
});